'use client'

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'

interface AIAnalysisModalProps {
  isOpen: boolean
  onClose: () => void
  analysis: string | null
  loading: boolean
  error: string | null
  symbol: string
  timeframe: string
  currentPrice?: number
  onRetry: () => void
}

const AIAnalysisModal: React.FC<AIAnalysisModalProps> = ({
  isOpen,
  onClose,
  analysis,
  loading,
  error,
  symbol,
  timeframe,
  currentPrice,
  onRetry
}) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen || !mounted) return null

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)'
      }}
      onClick={onClose}
    >
      <div 
        className="relative max-w-4xl max-h-[90vh] w-[90vw] m-4 rounded-xl overflow-hidden shadow-2xl flex flex-col"
        style={{
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="px-6 py-4 border-b"
          style={{
            background: 'linear-gradient(90deg, #2d5a87 0%, #4a7c59 100%)',
            borderColor: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-xl">🤖</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">AI Chart Analysis</h2>
                <div className="text-sm text-gray-300">
                  {symbol} • {timeframe}
                  {currentPrice && <span> • ${currentPrice.toFixed(2)}</span>}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center text-white transition-all duration-200"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white mt-4 mb-2">Analyzing Chart...</h3>
              <p className="text-gray-300 text-center">
                Our AI is examining the chart patterns, trends, and technical indicators to provide comprehensive analysis.
              </p>
              <div className="mt-6 flex items-center space-x-2 text-sm text-gray-400">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span>Processing chart data</span>
              </div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="w-16 h-16 bg-red-500 bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl text-red-400">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Analysis Failed</h3>
              <p className="text-gray-300 text-center mb-6">{error}</p>
              <button
                onClick={onRetry}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          )}

          {analysis && !loading && !error && (
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              <div className="p-6">
                {/* Analysis Content */}
                <div 
                  className="prose prose-invert max-w-none"
                  style={{
                    '--tw-prose-headings': '#ffffff',
                    '--tw-prose-body': '#e5e7eb',
                    '--tw-prose-bold': '#ffffff',
                    '--tw-prose-links': '#60a5fa',
                    '--tw-prose-bullets': '#9ca3af',
                    '--tw-prose-code': '#fbbf24'
                  } as any}
                >
                  <ReactMarkdown
                    components={{
                      h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-2xl font-bold text-white mb-4 pb-2 border-b border-gray-600">{children}</h1>,
                      h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-xl font-semibold text-white mb-3 mt-6">{children}</h2>,
                      h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-lg font-medium text-white mb-2 mt-4">{children}</h3>,
                      p: ({ children }: { children?: React.ReactNode }) => <p className="text-gray-300 mb-3 leading-relaxed">{children}</p>,
                      ul: ({ children }: { children?: React.ReactNode }) => <ul className="text-gray-300 mb-4 space-y-1">{children}</ul>,
                      li: ({ children }: { children?: React.ReactNode }) => <li className="flex items-start"><span className="text-blue-400 mr-2 mt-1">•</span><span>{children}</span></li>,
                      strong: ({ children }: { children?: React.ReactNode }) => <strong className="text-white font-semibold">{children}</strong>,
                      code: ({ children }: { children?: React.ReactNode }) => <code className="bg-gray-800 text-yellow-400 px-1 py-0.5 rounded text-sm">{children}</code>
                    }}
                  >
                    {analysis}
                  </ReactMarkdown>
                </div>

                {/* Disclaimer */}
                <div 
                  className="mt-8 p-4 rounded-lg border"
                  style={{
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderColor: 'rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <div className="flex items-start space-x-2">
                    <span className="text-blue-400 mt-0.5">ℹ️</span>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-300 mb-1">Important Disclaimer</h4>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        This AI analysis is for educational and informational purposes only. It should not be considered as financial advice. 
                        Always conduct your own research and consider consulting with a qualified financial advisor before making investment decisions. 
                        Trading involves substantial risk and may not be suitable for all investors.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {/* {analysis && !loading && !error && (
          <div 
            className="px-6 py-4 border-t"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderColor: 'rgba(255, 255, 255, 0.1)'
            }}
          >
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-400">
                Analysis generated at {new Date().toLocaleString()}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onRetry}
                  className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                >
                  🔄 Refresh Analysis
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm bg-[#21728d] hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )} */}
      </div>
    </div>,
    document.body
  )
}

export default AIAnalysisModal 